import { inject, Injectable } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import { select, Store } from '@ngrx/store';
import {
  ErrorStore,
  Notification,
  UnreadHorseNotification,
} from '@sleipai/sleip-models';
import { distinctUntilChanged, map, Observable } from 'rxjs';

import * as NotificationActions from '../actions/notification.actions';
import * as NotificationsReducer from '../reducers/notification.reducer';
import * as NotificationSelectors from '../selectors/notification.selectors';
import * as CountsByHorseSelectors from '../selectors/unread-notification-counts-by-horse.selectors';
import * as ForRecordingsUnreadSelectors from '../selectors/notifications-for-recordings-unread.selectors';

const activeRecordingNotification = (
  entities: Dictionary<Notification>,
  recordingId: string,
  userId?: string
): Notification | null => {
  const notification = entities[recordingId] ?? null;
  return notification && userId && notification.userId !== userId
    ? null
    : notification;
};

const isReadByUser = (notification: Notification | null): boolean =>
  notification ? notification.viewStatus ?? true : true;

@Injectable()
export class NotificationsStoreFacade {
  private readonly store = inject(Store<NotificationsReducer.State>);

  watchActiveRecordingNotification = (recordingId: string, userId: string) =>
    this.store.dispatch(
      NotificationActions.watchActiveRecordingNotification({
        recordingId,
        userId,
      })
    );

  stopWatchingActiveRecordingNotification = () =>
    this.store.dispatch(
      NotificationActions.stopWatchingActiveRecordingNotification()
    );

  getActiveRecordingNotification$ = (
    recordingId: string,
    userId: string
  ): Observable<Notification | null> =>
    this.store.pipe(
      select(NotificationSelectors.selectActiveRecordingNotificationEntities),
      map((entities) =>
        activeRecordingNotification(entities, recordingId, userId)
      ),
      distinctUntilChanged()
    );

  activeRecordingReadState$ = (
    recordingId: string,
    userId: string
  ): Observable<boolean | undefined> =>
    this.store.pipe(
      select(NotificationSelectors.selectActiveRecordingReadContext),
      map(({ watchedRecordingId, loading, entities }) => {
        if (watchedRecordingId !== recordingId || loading) {
          return undefined;
        }
        return isReadByUser(
          activeRecordingNotification(entities, recordingId, userId)
        );
      }),
      distinctUntilChanged()
    );

  isRecordingReadByUser$ = (
    recordingId: string,
    userId: string
  ): Observable<boolean> =>
    this.getActiveRecordingNotification$(recordingId, userId).pipe(
      map(isReadByUser),
      distinctUntilChanged()
    );

  markRecordingRead = (recordingId: string, userId: string, read = true) =>
    this.store.dispatch(
      NotificationActions.markRecordingNotificationRead({
        recordingId,
        userId,
        read,
      })
    );

  getIsLoading$: Observable<boolean> = this.store.pipe(
    select(NotificationSelectors.selectIsLoading)
  );

  getIsLoaded$: Observable<boolean> = this.store.pipe(
    select(NotificationSelectors.selectIsLoaded)
  );

  getIsMarkingRead$: Observable<boolean> = this.store.pipe(
    select(NotificationSelectors.selectIsMarkingRead)
  );

  getNotificationsError$: Observable<ErrorStore | null> = this.store.pipe(
    select(NotificationSelectors.selectNotificationsError)
  );

  watchNotificationsForRecordingsUnread = (
    recordingIds: string[],
    userId: string
  ) =>
    this.store.dispatch(
      NotificationActions.watchNotificationsForRecordingsUnread({
        recordingIds,
        userId,
      })
    );

  stopWatchingNotificationsForRecordingsUnread = () =>
    this.store.dispatch(
      NotificationActions.stopWatchingNotificationsForRecordingsUnread()
    );

  getNotificationsForRecordingsUnreadIds$: Observable<Record<string, boolean>> =
    this.store.pipe(
      select(ForRecordingsUnreadSelectors.selectNotificationsForRecordingsUnreadIds)
    );

  getIsNotificationsForRecordingsUnreadLoading$: Observable<boolean> =
    this.store.pipe(
      select(ForRecordingsUnreadSelectors.selectIsNotificationsForRecordingsUnreadLoading)
    );

  getIsNotificationsForRecordingsUnreadLoaded$: Observable<boolean> =
    this.store.pipe(
      select(ForRecordingsUnreadSelectors.selectIsNotificationsForRecordingsUnreadLoaded)
    );

  getNotificationsForRecordingsUnreadError$: Observable<ErrorStore | null> =
    this.store.pipe(
      select(ForRecordingsUnreadSelectors.selectNotificationsForRecordingsUnreadError)
    );

  watchUnreadNotificationCountsByHorse = (
    horseIds: string[],
    userId: string
  ) =>
    this.store.dispatch(
      NotificationActions.watchUnreadNotificationCountsByHorse({
        horseIds,
        userId,
      })
    );

  stopWatchingUnreadNotificationCountsByHorse = () =>
    this.store.dispatch(
      NotificationActions.stopWatchingUnreadNotificationCountsByHorse()
    );

  getUnreadNotificationCountForHorse$ = (
    horseId: string
  ): Observable<number> =>
    this.store.pipe(
      select(CountsByHorseSelectors.selectUnreadHorseNotificationEntities),
      map((entities) => entities[horseId]?.unreadCount ?? 0),
      distinctUntilChanged()
    );

  getHasUnreadNotificationsForHorse$ = (
    horseId: string
  ): Observable<boolean> =>
    this.getUnreadNotificationCountForHorse$(horseId).pipe(
      map((count) => count > 0),
      distinctUntilChanged()
    );

  getUnreadNotificationCountsByHorse$: Observable<Record<string, number>> =
    this.store.pipe(
      select(CountsByHorseSelectors.selectUnreadNotificationCountsByHorse)
    );

  getAllUnreadNotificationCountsByHorse$: Observable<UnreadHorseNotification[]> =
    this.store.pipe(
      select(CountsByHorseSelectors.selectAllUnreadNotificationCountsByHorse)
    );

  getTotalUnreadNotificationCount$: Observable<number> = this.store.pipe(
    select(CountsByHorseSelectors.selectTotalUnreadNotificationCount)
  );

  getIsUnreadNotificationCountsLoading$: Observable<boolean> = this.store.pipe(
    select(CountsByHorseSelectors.selectIsUnreadNotificationCountsLoading)
  );

  getIsUnreadNotificationCountsLoaded$: Observable<boolean> = this.store.pipe(
    select(CountsByHorseSelectors.selectIsUnreadNotificationCountsLoaded)
  );

  getUnreadNotificationCountsError$: Observable<ErrorStore | null> =
    this.store.pipe(
      select(CountsByHorseSelectors.selectUnreadNotificationCountsError)
    );

  clearError = () =>
    this.store.dispatch(NotificationActions.clearNotificationsError());
}
